"use client";

import React from "react";
import { DesignSystemFixtureMetadata } from "@features/design-system/design-system-types";
import { FixtureInvariantMeta } from "@features/design-system/ui/fixture/fixture-invariant-meta";
import { FixtureLibsUsed } from "@features/design-system/ui/fixture/fixture-libs-used";
import { FixtureMetadata } from "@features/design-system/ui/fixture/fixture-metadata";
import { FixturePropertyImpactTable } from "@features/design-system/ui/fixture/fixture-property-impact-table";
import {
  FixturePropsTable,
  PropertyDefinition,
} from "@features/design-system/ui/fixture/fixture-props-table";
import { FixtureSection } from "@features/design-system/ui/fixture/fixture-section";
import { FixtureWrapper } from "@features/design-system/ui/fixture/fixture-wrapper";
import { ActivityMemberEntity } from "@features/proposals/types/member";
import { ActivityMemberContentLocalizableViewBlock } from "@features/proposals/ui/member/member-content-localizable-view-block";

// Хелпер для локализуемых полей (LocaleMessageType)
const R = (ru: string, en = ru) => ({ ru, en });

// Базовый участник с полными данными
const baseMember: ActivityMemberEntity = {
  id: 1,
  isMe: true,
  role: "SPEAKER",
  rights: { canPatch: true },
  eventProfile: {
    biography: {
      content: {
        data: {
          id: { iv: 22001608 },
          ownerId: { iv: 10715930 },
          firstName: R("Николай", "Nikolay"),
          lastName: R("Молчанов", "Molchanov"),
          alias: R("nmolchanov"),
          company: R("JUG Ru Group"),
          position: R("CTO"),
          titulus: { iv: "Java Champion" },
          description: R(
            "Руководитель разработки JUG Ru Group. Архитектор online платформы конференций. Fullstack разработчик на платформах .NET и JavaScript. Ментейнер проекта Flurl. Придерживается принципов разработки DDD и TDD.",
            "Head of Development at JUG Ru Group. Architect of online conference platform. Fullstack developer on .NET and JavaScript platforms. Maintainer of the Flurl project. Adheres to DDD and TDD development principles.",
          ),
          contacts: { iv: [{ type: "twitter", value: "@nmolchanov" }] },
          photo: { iv: ["https://placehold.co/200x200?text=NM"] },
        },
        assets: [
          {
            id: "photo-1",
            links: { content: "https://placehold.co/200x200?text=NM" },
            createdAt: new Date("2024-01-01T00:00:00Z"),
          },
        ],
      },
    },
    consent: {
      content_publishing: {
        accepted: true,
        acceptedAt: new Date("2024-01-15T10:00:00Z"),
        acceptedByName: "Николай Молчанов",
      },
    },
    notifee: {
      welcome_notification: {
        accepted: true,
        acceptedAt: new Date("2024-01-15T10:00:00Z"),
      },
      working_out_notification: {
        accepted: true,
        acceptedAt: new Date("2024-01-15T10:00:00Z"),
      },
      proposal_status_notification: {
        accepted: false,
      },
    },
  },
};

// Участник без фото
const memberWithoutPhoto: ActivityMemberEntity = {
  ...baseMember,
  id: 2,
  eventProfile: {
    ...baseMember.eventProfile,
    biography: {
      content: {
        data: {
          ...baseMember.eventProfile!.biography!.content!.data,
          photo: { iv: [] },
        },
        assets: [],
      },
    },
  },
};

// Участник с минимальными данными
const memberMinimal: ActivityMemberEntity = {
  id: 3,
  isMe: false,
  role: "EXPERT",
  rights: { canPatch: false },
  eventProfile: {
    biography: {
      content: {
        data: {
          id: { iv: "bio-3" },
          ownerId: { iv: "owner-3" },
          firstName: R("Иван", "Ivan"),
          lastName: R("Петров", "Petrov"),
          alias: R(""),
          company: R(""),
          position: R(""),
          titulus: { iv: undefined },
          description: R(""),
          contacts: { iv: [] },
          photo: { iv: [] },
        },
        assets: [],
      },
    },
    consent: {
      content_publishing: {
        accepted: false,
      },
    },
  },
};

// Участник с отклонённым согласием на публикацию
const memberNoConsent: ActivityMemberEntity = {
  ...baseMember,
  id: 6,
  eventProfile: {
    ...baseMember.eventProfile,
    consent: {
      content_publishing: {
        accepted: false,
      },
    },
  },
};

// Участник без данных о согласии (consent отсутствует)
const memberNoConsentData: ActivityMemberEntity = {
  ...baseMember,
  id: 4,
  eventProfile: {
    ...baseMember.eventProfile,
    consent: undefined, // Отсутствие данных о согласии
  },
};

// Участник HOST с длинной биографией
const memberHost: ActivityMemberEntity = {
  ...baseMember,
  id: 5,
  role: "HOST",
  eventProfile: {
    ...baseMember.eventProfile,
    biography: {
      content: {
        data: {
          ...baseMember.eventProfile!.biography!.content!.data,
          description: R(
            "Очень длинная биография которая содержит более 700 символов текста для демонстрации работы режима сокращения. ".repeat(
              10,
            ),
            "Very long biography that contains more than 700 characters of text to demonstrate the shortMode feature. ".repeat(
              10,
            ),
          ),
        },
      },
    },
  },
};

// Документация свойств публичного API
const generalProps: PropertyDefinition[] = [
  {
    name: "member",
    type: "ActivityMemberEntity",
    defaultValue: "undefined",
    description:
      "Доменная сущность участника активности. Содержит биографические данные, согласия, настройки уведомлений и права доступа.",
  },
  {
    name: "patchMember",
    type: "(member, body) => Promise<ActivityMemberEntity | undefined>",
    defaultValue: "undefined",
    description:
      "Функция для обновления данных участника. Используется в MemberConsentsDialog для сохранения изменений согласий.",
  },
  {
    name: "isSingleMember",
    type: "boolean",
    defaultValue: "undefined",
    description:
      "Флаг, указывающий, является ли участник единственным в активности. Если true, кнопка удаления будет отключена (disabled) для предотвращения удаления последнего участника.",
  },
  {
    name: "useShortMode",
    type: "boolean",
    defaultValue: "false",
    description:
      "Включает режим сокращения длинной биографии до 300 символов с кнопкой 'Показать еще'.",
  },
  {
    name: "actions",
    type: "ReactNode",
    defaultValue: "undefined",
    description:
      "Дополнительные действия, отображаемые в заголовке блока рядом с переключателем языка, бейджем согласий и кнопкой удаления.",
  },
];

const ActivityMemberContentLocalizableViewBlockFixture = () => (
  <>
    {/* 1. Описание */}
    <FixtureSection title="1. Описание" id="about">
      <div className="space-y-4">
        <p>
          Компонент <strong>ActivityMemberContentLocalizableViewBlock</strong> отображает полную
          информацию о члене активности (спикере, эксперте, ведущем) для публикации на сайте
          конференции. Включает фотографию, биографические данные, роль, контакты и ссылку на
          Twitter.
        </p>
        <p>
          <strong>Когда использовать:</strong> На вкладке Участники в карточке активности, в разделе
          Speaker Area для отображения информации о спикерах мероприятия.
        </p>
        <p>
          <strong>Ключевые особенности:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Многоязычность: переключение между русским и английским контентом</li>
          <li>Автоматическая обработка отсутствующих данных (отображение прочерка -)</li>
          <li>Фотография спикера с масштабированием и fallback на инициалы</li>
          <li>Бейдж роли с тултипом-описанием</li>
          <li>
            Бейдж статуса согласия на публикацию с диалоговым окном деталей (условная видимость: не
            отображается, если данные о согласии отсутствуют)
          </li>
          <li>
            Кнопка перехода в карточку контрибьютора (условная видимость: отображается только при
            наличии contributorId; открывает боковую панель с детальной информацией)
          </li>
          <li>
            Кнопка удаления участника с подтверждением (условная видимость: отображается только при
            наличии права canDelete; кнопка disabled если участник единственный)
          </li>
          <li>Режим сокращения длинных биографий (useShortMode)</li>
          <li>Адаптивная сетка для полей биографии (2 колонки на десктопе)</li>
        </ul>
      </div>
    </FixtureSection>

    {/* 2. Meta */}
    <FixtureSection title="2. Meta" id="meta">
      <FixtureMetadata
        componentName="ActivityMemberContentLocalizableViewBlock"
        sourcePath="src/features/proposals/ui/member/member-content-localizable-view-block.tsx"
        domain="Proposals → Activity → Members"
        dependencies={[
          {
            path: "@components/common/content/content-view-block",
            description: "Базовый контейнер для блоков просмотра контента",
          },
          {
            path: "@components/common/buttons/switch-locale-toggle",
            description: "Переключатель языков",
          },
          {
            path: "@features/proposals/ui/member/member-photo-view",
            description: "Компонент отображения фотографии участника",
          },
          {
            path: "@features/proposals/ui/member/member-role-badge",
            description: "Бейдж роли с тултипом",
          },
          {
            path: "@features/proposals/ui/member/member-consents-dialog",
            description: "Диалог с деталями согласий и уведомлений",
          },
          {
            path: "@features/proposals/ui/member/member-delete-block",
            description: "Компонент удаления участника с подтверждением",
          },
          {
            path: "@features/contributors/controllers/contributor-controller",
            description: "Контроллер отображения детальной информации о контрибьюторе",
          },
          {
            path: "@components/common/containers/side-panel",
            description: "Боковая панель для встроенного отображения контента",
          },
          {
            path: "@lib/i18n/utils",
            description: "Утилиты локализации (localizeFrom)",
          },
          {
            path: "@lib/content",
            description: "Инструменты обработки HTML-контента",
          },
          {
            path: "@features/proposals/proposals-tools",
            description: "Бизнес-утилиты для работы с участниками",
          },
        ]}
        version="1.0.0"
      />
    </FixtureSection>

    {/* 3. Бизнес-контекст */}
    <FixtureSection title="3. Бизнес‑контекст" id="business-context">
      <div className="space-y-4">
        <p>
          <strong>Роли и сценарии использования:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Спикер (isMe = true):</strong> Просмотр и редактирование своей биографии в
            личном кабинете. Ожидаемая ценность: быстрый доступ к редактированию, проверка данных
            перед публикацией, управление согласиями.
          </li>
          <li>
            <strong>Организатор/Куратор:</strong> Просмотр информации о всех участниках активности.
            Ожидаемая ценность: контроль полноты данных, проверка согласий на публикацию, мониторинг
            статусов уведомлений.
          </li>
          <li>
            <strong>Редактор контента:</strong> Проверка качества биографий для публикации.
            Ожидаемая ценность: многоязычный контент, возможность копирования, визуальная проверка
            всех полей.
          </li>
          <li>
            <strong>Программный комитет:</strong> Оценка профиля спикера при рассмотрении заявки.
            Ожидаемая ценность: полная информация о спикере, профессиональные звания, социальные
            контакты.
          </li>
          <li>
            <strong>Куратор активности:</strong> Управление составом участников
            (добавление/удаление). Ожидаемая ценность: возможность удаления участников с
            подтверждением, защита от удаления последнего участника, быстрый переход к полной
            информации о контрибьюторе.
          </li>
        </ul>
        <p>
          <strong>Ключевые ограничения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Все поля отображаются всегда, даже если данные отсутствуют (показывается -)</li>
          <li>
            Локализация доступна только для языков, присутствующих в данных биографии (не все языки
            системы)
          </li>
          <li>
            Режим сокращения (useShortMode) применяется только к полю description, если оно
            превышает 300 символов
          </li>
          <li>
            Кнопка перехода к контрибьютору отображается только при наличии contributorId (открывает
            боковую панель с детальной информацией)
          </li>
          <li>
            Кнопка удаления отображается только при наличии права canDelete и автоматически
            отключается, если участник единственный (isSingleMember = true)
          </li>
          <li>Фотография масштабируется автоматически с кешированием на 1 час</li>
          <li>Бейдж согласия на публикацию отражает только основной статус (принято/не принято)</li>
          <li>Роль участника отображается только если указана (SPEAKER, EXPERT, HOST)</li>
        </ul>
      </div>
    </FixtureSection>

    {/* 4. Пропсы и типы */}
    <FixtureSection title="4. Пропсы и типы" id="props-types">
      <FixturePropsTable properties={generalProps} />
      <div className="mt-4 space-y-3">
        <FixturePropertyImpactTable
          properties={[
            {
              propertyPath: "member.eventProfile.biography.content.data.firstName",
              type: "LocaleMessageType",
              renderImpact: "Имя участника (обязательное поле)",
              missingBehavior: "Отображается прочерк -",
            },
            {
              propertyPath: "member.eventProfile.biography.content.data.lastName",
              type: "LocaleMessageType",
              renderImpact: "Фамилия участника (обязательное поле)",
              missingBehavior: "Отображается прочерк -",
            },
            {
              propertyPath: "member.eventProfile.biography.content.data.photo",
              type: "AssetId",
              renderImpact: "Фотография участника",
              missingBehavior: "Отображается аватар с инициалами",
            },
            {
              propertyPath: "member.role",
              type: "ActivityMemberRole",
              renderImpact: "Бейдж роли в правом верхнем углу",
              missingBehavior: "Бейдж не отображается",
            },
            {
              propertyPath: "member.eventProfile.consent.content_publishing",
              type: "ContributorFormOutputView",
              renderImpact: "Статус в бейдже согласий (цвет и текст)",
              missingBehavior: "Считается как не принято",
            },
            {
              propertyPath: "member.eventProfile.biography.content.data.description",
              type: "LocaleMessageType",
              renderImpact: "Биография участника (поддерживает HTML)",
              missingBehavior: "Блок биографии не отображается",
            },
            {
              propertyPath: "useShortMode",
              type: "boolean",
              renderImpact: "Сокращение биографии до 300 символов с кнопкой",
              missingBehavior: "По умолчанию false, биография полностью видна",
            },
            {
              propertyPath: "member.contributorId",
              type: "Identifier",
              renderImpact: "Видимость кнопки перехода к контрибьютору",
              missingBehavior: "Кнопка не отображается",
            },
            {
              propertyPath: "member.rights.canDelete",
              type: "boolean",
              renderImpact: "Видимость кнопки удаления участника",
              missingBehavior: "Кнопка удаления не отображается",
            },
            {
              propertyPath: "isSingleMember",
              type: "boolean",
              renderImpact: "Состояние кнопки удаления (enabled/disabled)",
              missingBehavior: "Кнопка активна (можно удалить)",
            },
          ]}
        />
      </div>
    </FixtureSection>

    {/* 5. Инварианты */}
    <FixtureSection title="5. Инварианты" id="invariants">
      {/* 5.1 Базовый */}
      <FixtureSection title="5.1 Базовый рендеринг" id="invariant-base" variant="inner">
        <FixtureInvariantMeta
          id="base-render"
          name="Полные данные участника"
          goal="Проверка стандартного рендеринга с полным набором биографических данных и согласиями."
          preconditions="Все поля биографии заполнены, согласие на публикацию получено, роль указана."
          inputData={[
            "firstName/lastName: локализованные строки",
            "role: SPEAKER",
            "photo: массив URL изображений",
            "consent.content_publishing.accepted: true",
            "все дополнительные поля: заполнены",
          ]}
          expectedResult="Отображаются: фотография, имя, фамилия, бейдж роли SPEAKER, псевдоним, компания, позиция, звание, Twitter, биография. Бейдж согласия с default-вариантом (черный) и текстом 'Получено'."
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "member.isMe",
              type: "boolean",
              value: "true",
              description: "Текущий пользователь",
            },
            {
              name: "member.role",
              type: "ActivityMemberRole",
              value: '"SPEAKER"',
              description: "Роль спикера",
            },
            {
              name: "member.eventProfile.biography",
              type: "ContributorBiographyInlineOutputView",
              value: "{ полные данные }",
              description: "Полная биография",
            },
          ]}
        />
        <FixtureWrapper label="Базовый" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={baseMember}
            patchMember={async () => baseMember}
            isSingleMember={false}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.2 Без фотографии */}
      <FixtureSection title="5.2 Без фотографии" id="invariant-no-photo" variant="inner">
        <FixtureInvariantMeta
          id="no-photo"
          name="Участник без фотографии"
          goal="Проверка корректной деградации при отсутствии фотографии."
          preconditions="Поле photo пустое или отсутствует."
          inputData={["photo.iv: []", "firstName/lastName: заполнены"]}
          expectedResult="Отображается аватар с инициалами имени и фамилии. Остальные поля без изменений."
          edgeCases="Если имя или фамилия отсутствуют, используются доступные инициалы или '?'."
        />
        <FixtureWrapper label="Без фото" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={memberWithoutPhoto}
            patchMember={async () => memberWithoutPhoto}
            isSingleMember={false}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.3 Минимальные данные */}
      <FixtureSection title="5.3 Минимальные данные" id="invariant-minimal" variant="inner">
        <FixtureInvariantMeta
          id="minimal-data"
          name="Минимальный набор данных"
          goal="Проверка отображения с минимальными обязательными данными."
          preconditions="Заполнены только firstName и lastName."
          inputData={["firstName/lastName: заполнены", "все остальные поля: пустые или undefined"]}
          expectedResult='Отображаются имя и фамилия. Все остальные поля показывают прочерк "-". Бейдж согласия показывает "Не получено".'
          edgeCases="Компонент должен корректно работать без photo, Twitter, позиции, компании и т.д."
        />
        <FixtureWrapper label="Минимальные данные" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={memberMinimal}
            patchMember={async () => memberMinimal}
            isSingleMember={false}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.4 Без данных о согласии */}
      <FixtureSection
        title="5.4 Без данных о согласии"
        id="invariant-no-consent-data"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="no-consent-data"
          name="Отсутствие данных о согласии"
          goal="Проверка условной видимости бейджа согласий при отсутствии данных."
          preconditions="consent отсутствует (undefined) или eventProfile отсутствует."
          inputData={["consent: undefined"]}
          expectedResult="Бейдж согласий не отображается (возвращается null). Остальной контент рендерится нормально."
          edgeCases="Компонент MemberConsentsDialog выполняет проверку isAccepted === null и возвращает null, скрывая бейдж."
        />
        <FixtureWrapper label="Без данных о согласии" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={memberNoConsentData}
            patchMember={async () => memberNoConsentData}
            isSingleMember={false}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.5 Отклонённое согласие на публикацию */}
      <FixtureSection
        title="5.5 Отклонённое согласие"
        id="invariant-rejected-consent"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="rejected-consent"
          name="Отклонённое согласие на публикацию"
          goal="Проверка визуального отображения статуса отклонённого согласия."
          preconditions="consent.content_publishing.accepted = false."
          inputData={["consent.content_publishing.accepted: false"]}
          expectedResult='Бейдж согласий показывает "Не получено" с destructive-вариантом (красный). При клике открывается диалог с деталями.'
          edgeCases="Состояние согласия влияет только на визуал бейджа, не блокирует отображение данных."
        />
        <FixtureWrapper label="Отклонённое согласие" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={memberNoConsent}
            patchMember={async () => memberNoConsent}
            isSingleMember={false}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.6 Режим сокращения длинной биографии */}
      <FixtureSection title="5.6 Режим сокращения" id="invariant-short-mode" variant="inner">
        <FixtureInvariantMeta
          id="short-mode"
          name="Режим сокращения длинной биографии"
          goal="Проверка работы useShortMode для длинных биографий."
          preconditions="description длиннее 700 символов, useShortMode = true."
          inputData={["description: 1000+ символов", "useShortMode: true"]}
          expectedResult='Биография обрезается до 300 символов с "..." и кнопкой "Показать еще" в футере. При клике отображается полный текст.'
          edgeCases="HTML-разметка в биографии сохраняется и корректно рендерится."
        />
        <FixtureWrapper label="Короткий режим" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={memberHost}
            patchMember={async () => memberHost}
            isSingleMember={false}
            useShortMode
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.7 Разные роли участников */}
      <FixtureSection title="5.7 Роли участников" id="invariant-roles" variant="inner">
        <FixtureInvariantMeta
          id="member-roles"
          name="Бейджи ролей: SPEAKER, EXPERT, HOST"
          goal="Проверка визуального отображения разных ролей участников."
          preconditions="Роль указана в member.role."
          inputData={[
            'role: "SPEAKER" → бейдж "Спикер"',
            'role: "EXPERT" → бейдж "Эксперт"',
            'role: "HOST" → бейдж "Ведущий"',
          ]}
          expectedResult="Бейдж роли отображается в правой части строки с именем. При наведении показывается тултип с описанием роли."
          edgeCases="Если роль отсутствует, бейдж не отображается."
        />
        <FixtureWrapper label="Роли" variant="default">
          <div className="flex flex-col gap-4">
            <ActivityMemberContentLocalizableViewBlock
              member={{ ...baseMember, id: 10, role: "SPEAKER" }}
              patchMember={async () => baseMember}
              isSingleMember={false}
            />
            <ActivityMemberContentLocalizableViewBlock
              member={{ ...baseMember, id: 11, role: "EXPERT" }}
              patchMember={async () => baseMember}
              isSingleMember={false}
            />
            <ActivityMemberContentLocalizableViewBlock
              member={{ ...baseMember, id: 12, role: "HOST" }}
              patchMember={async () => baseMember}
              isSingleMember={false}
            />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.8 Переход к контрибьютору */}
      <FixtureSection
        title="5.8 Переход к контрибьютору"
        id="invariant-contributor"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="contributor-navigation"
          name="Кнопка перехода к карточке контрибьютора"
          goal="Проверка работы навигации к детальной информации о контрибьюторе через боковую панель."
          preconditions="member.contributorId присутствует."
          inputData={[
            "member.contributorId: numeric ID",
            "firstName/lastName: заполнены для заголовка панели",
          ]}
          expectedResult="Кнопка с иконкой пользователя (IconUser) отображается в заголовке блока. При клике открывается боковая панель размера xl с заголовком из полного имени участника и контроллером ContributorController, отображающим все активности, проекты и статистику контрибьютора."
          edgeCases="Если contributorId отсутствует, кнопка не отображается. Панель имеет уникальный ключ contributor-{contributorId} для корректного управления состоянием."
        />
        <FixturePropsTable
          properties={[
            {
              name: "member.contributorId",
              type: "Identifier",
              value: "10715930",
              description: "ID контрибьютора для навигации",
            },
          ]}
        />
        <FixtureWrapper label="С contributorId (кнопка видна)" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={{ ...baseMember, contributorId: 10715930 }}
            patchMember={async () => baseMember}
            isSingleMember={false}
          />
        </FixtureWrapper>
        <FixtureWrapper label="Без contributorId (кнопка скрыта)" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={{ ...baseMember, contributorId: undefined }}
            patchMember={async () => baseMember}
            isSingleMember={false}
          />
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.9 Удаление участника */}
      <FixtureSection title="5.9 Удаление участника" id="invariant-delete" variant="inner">
        <FixtureInvariantMeta
          id="member-delete"
          name="Удаление участника с подтверждением"
          goal="Проверка работы кнопки удаления участника и диалога подтверждения."
          preconditions="member.rights.canDelete = true, isSingleMember = false."
          inputData={[
            "member.rights.canDelete: true",
            "isSingleMember: false",
            "firstName/lastName: заполнены для отображения имени в диалоге",
          ]}
          expectedResult="Кнопка удаления (иконка корзины) отображается в заголовке блока. При клике открывается диалог подтверждения с именем участника и предупреждением о необратимости действия. После подтверждения участник удаляется через API."
          edgeCases="Если canDelete = false, кнопка не отображается. Если isSingleMember = true, кнопка отключена (disabled) с соответствующим тултипом."
        />
        <FixturePropsTable
          properties={[
            {
              name: "member.rights.canDelete",
              type: "boolean",
              value: "true",
              description: "Право на удаление участника",
            },
            {
              name: "isSingleMember",
              type: "boolean",
              value: "false",
              description: "Флаг единственного участника",
            },
          ]}
        />
        <FixtureWrapper label="Удаление (доступно)" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={{ ...baseMember, rights: { ...baseMember.rights, canDelete: true } }}
            patchMember={async () => baseMember}
            isSingleMember={false}
          />
        </FixtureWrapper>
        <FixtureWrapper label="Удаление (заблокировано - единственный участник)" variant="default">
          <ActivityMemberContentLocalizableViewBlock
            member={{ ...baseMember, rights: { ...baseMember.rights, canDelete: true } }}
            patchMember={async () => baseMember}
            isSingleMember={true}
          />
        </FixtureWrapper>
      </FixtureSection>
    </FixtureSection>

    {/* 6. Примеры и edge-cases */}
    <FixtureSection title="6. Примеры и крайние случаи" id="edge-cases">
      <div className="space-y-4">
        <p>
          <strong>Полнота данных:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Полные данные:</strong> Все поля заполнены → полноценный рендер с фотографией,
            контактами, звани ем.
          </li>
          <li>
            <strong>Минимальные данные:</strong> Только firstName/lastName → остальные поля
            показывают -.
          </li>
          <li>
            <strong>Пустые локализации:</strong> Если локаль отсутствует в данных → переключатель
            языка не показывает эту локаль.
          </li>
          <li>
            <strong>Очень длинная биография:</strong> 2000+ символов в description → useShortMode
            сокращает до 300 с кнопкой.
          </li>
          <li>
            <strong>Единственный участник:</strong> isSingleMember = true → кнопка удаления
            отображается, но заблокирована (disabled).
          </li>
          <li>
            <strong>Переход к контрибьютору:</strong> contributorId присутствует → отображается
            кнопка с иконкой пользователя, открывающая боковую панель.
          </li>
          <li>
            <strong>Нет contributorId:</strong> contributorId отсутствует → кнопка перехода к
            контрибьютору не отображается.
          </li>
          <li>
            <strong>Нет права на удаление:</strong> canDelete = false → кнопка удаления не
            отображается вовсе.
          </li>
          <li>
            <strong>HTML в биографии:</strong> Поддерживается, рендерится через
            dangerouslySetInnerHTML после санитизации.
          </li>
        </ul>
        <p>
          <strong>Специальные случаи:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Эмодзи в биографии отображаются корректно.</li>
          <li>Длинные ссылки Twitter обрезаются визуально, но остаются кликабельными.</li>
          <li>Профессиональные звания (Java Champion и др.) отображаются без перевода.</li>
          <li>
            <strong>Бейдж согласий:</strong> Не отображается, если consent отсутствует в данных
            участника. Показывается красный destructive-бейдж при accepted = false. Показывается
            зелёный default-бейдж при accepted = true.
          </li>
          <li>
            <strong>Кнопка перехода к контрибьютору:</strong> Отображается только при наличии
            contributorId. При клике открывает боковую панель размера xl с контроллером
            ContributorController, отображающим детальную информацию о контрибьюторе (все его
            активности, проекты, статистику). Заголовок панели содержит полное имя участника.
          </li>
          <li>
            <strong>Кнопка удаления:</strong> Отображается только при canDelete = true. Disabled при
            isSingleMember = true с тултипом-предупреждением. При клике показывает диалог
            подтверждения с именем участника и предупреждением о необратимости.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 7. Accessibility */}
    <FixtureSection title="7. Доступность" id="accessibility">
      <ul className="ml-6 list-disc space-y-1">
        <li>Все интерактивные элементы (ссылки, кнопки, бейджи) доступны с клавиатуры.</li>
        <li>Фотография имеет alt-атрибут с именем участника.</li>
        <li>Переключатель языка работает через Tab и Enter/Space.</li>
        <li>Бейдж согласия открывает диалог через Enter/Space.</li>
        <li>Тултипы роли отображаются при фокусе с клавиатуры.</li>
        <li>Контрастность текста соответствует WCAG AA.</li>
        <li>Кнопка Показать еще в футере доступна через Tab.</li>
      </ul>
    </FixtureSection>

    {/* 8. Используемые библиотеки */}
    <FixtureSection title="8. Используемые библиотеки" id="design-system-used">
      <FixtureLibsUsed
        items={[
          { label: "React", href: "https://react.dev/" },
          { label: "Next.js (App Router)", href: "https://nextjs.org/docs/app" },
          { label: "next-intl", href: "https://next-intl-docs.vercel.app/" },
          { label: "shadcn/ui — Avatar", href: "https://ui.shadcn.com/docs/components/avatar" },
          { label: "shadcn/ui — Badge", href: "https://ui.shadcn.com/docs/components/badge" },
          { label: "shadcn/ui — Button", href: "https://ui.shadcn.com/docs/components/button" },
          { label: "shadcn/ui — Tooltip", href: "https://ui.shadcn.com/docs/components/tooltip" },
          {
            label: "shadcn/ui — Alert Dialog",
            href: "https://ui.shadcn.com/docs/components/alert-dialog",
          },
          { label: "Tailwind CSS", href: "https://tailwindcss.com/docs" },
          { label: "Tabler Icons", href: "https://tabler-icons.io/" },
          { label: "Lucide Icons", href: "https://lucide.dev/" },
        ]}
      />
    </FixtureSection>

    {/* 9. Рекомендации */}
    <FixtureSection title="9. Рекомендации" id="guidelines">
      <ul className="ml-6 list-disc space-y-1">
        <li>
          Всегда заполняйте firstName и lastName — это минимально необходимые данные для
          отображения.
        </li>
        <li>
          Используйте useShortMode = true для карточек в списках, где биография может быть длинной
          (порог сокращения - 300 символов).
        </li>
        <li>
          Всегда передавайте корректное значение isSingleMember для предотвращения удаления
          последнего участника активности.
        </li>
        <li>
          Обязательно получайте согласие на публикацию (content_publishing) перед отображением
          данных на публичном сайте.
        </li>
        <li>
          Фотографии должны быть квадратными (рекомендуется 200x200px минимум) для корректного
          отображения.
        </li>
        <li>Биография может содержать HTML, но она проходит санитизацию для безопасности.</li>
        <li>
          Роль участника (SPEAKER/EXPERT/HOST) влияет на визуальное восприятие и должна быть указана
          корректно.
        </li>
      </ul>
    </FixtureSection>

    {/* 10. Бизнес-правила */}
    <FixtureSection title="10. Бизнес‑правила" id="business-rules">
      <div className="space-y-3">
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Инвариант 1 (отображение полей): Все поля биографии отображаются всегда. При отсутствии
            данных показывается прочерк - для визуальной консистентности.
          </li>
          <li>
            Инвариант 2 (фотография): При отсутствии photo отображается аватар с инициалами,
            сгенерированными из firstName и lastName.
          </li>
          <li>
            Инвариант 3 (локализация): Переключатель языка показывает только языки, присутствующие в
            данных biography. Недоступные локали скрыты.
          </li>
          <li>
            Инвариант 4 (согласие): Статус consent.content_publishing.accepted определяет цвет и
            текст бейджа согласий. false → Не получено (destructive/красный), true → Получено
            (default/зелёный). Если consent отсутствует (undefined/null), бейдж не отображается
            вовсе (MemberConsentsDialog возвращает null).
          </li>
          <li>
            Инвариант 5 (роль): Бейдж роли отображается только если member.role указана.
            Поддерживаются SPEAKER, EXPERT, HOST.
          </li>
          <li>
            Инвариант 6 (биография): description рендерится как HTML через dangerouslySetInnerHTML
            после санитизации contentTools.getSanitizedContent().
          </li>
          <li>
            Инвариант 7 (сокращение): При useShortMode = true и description &gt; 300 символов текст
            обрезается с добавлением ... и кнопки Показать еще/Спрятать.
          </li>
          <li>
            Инвариант 8 (переход к контрибьютору): Кнопка с иконкой IconUser отображается только при
            наличии member.contributorId. При клике вызывается handleOpenContributor, который
            открывает боковую панель (useSidePanel) с уникальным ключом contributor-
            {`{contributorId}`}, заголовком из полного имени участника (firstName + lastName),
            размером xl и встроенным ContributorController для отображения детальной информации.
          </li>
          <li>
            Инвариант 9 (удаление): Кнопка удаления отображается только при member.rights.canDelete
            = true. Кнопка автоматически disabled при isSingleMember = true. При клике открывается
            AlertDialogWrapper с подтверждением, отображающим имя участника (firstName + lastName
            или ID) и предупреждением о необратимости.
          </li>
          <li>
            Инвариант 10 (Twitter): Ссылка на Twitter формируется из contacts массива, фильтруя type
            === twitter и убирая символ @ через proposalsTools.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 11. Примечания/допущения */}
    <FixtureSection title="11. Примечания/допущения" id="notes-assumptions">
      <div className="space-y-3">
        <p>
          <strong>Контекстуальные допущения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            Компонент использует next-intl для локализации и предполагает, что провайдер настроен на
            уровне приложения.
          </li>
          <li>
            Все переводы для полей biography, ролей и статусов согласий существуют в
            /messages/ru.json и /messages/en.json.
          </li>
          <li>
            Масштабирование фотографий выполняется через domainTools.imageActions.getScaledImageUrl
            с кешированием на 1 час.
          </li>
          <li>
            Бизнес-утилита proposalsTools.activityMemberTools.isMemberPublicationConsentAccepted
            корректно определяет статус согласия.
          </li>
          <li>
            HTML в биографии предполагается безопасным после санитизации
            contentTools.getSanitizedContent().
          </li>
        </ul>
        <p>
          <strong>TODO/риски:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Проверить наличие всех переводов для новых полей biography (titulus, position, alias) в
            обеих локалях.
          </li>
          <li>
            Уточнить бизнес-правило отображения при isMe = true vs false (влияет на доступность
            редактирования).
          </li>
          <li>
            Протестировать поведение с очень длинными значениями в полях (company, position &gt; 100
            символов).
          </li>
          <li>
            Подтвердить список поддерживаемых профессиональных званий (titulus) и их написание.
          </li>
        </ul>
      </div>
    </FixtureSection>
  </>
);

export default ActivityMemberContentLocalizableViewBlockFixture;

export const meta: DesignSystemFixtureMetadata = {
  group: "Proposals",
  parentItem: "Activity / Members",
  title: "Activity Member Content Block",
  description:
    "Локализуемый блок биографической информации участника активности с фотографией, ролью и согласиями",
};
